import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  SubscriptionBillingCadence,
  WorkspaceBillingActivityPageContract,
  WorkspaceBillingActivityType,
  WorkspaceBillingContract,
} from '@nest-react/domain';
import { useSearchParams } from 'react-router-dom';

import { requestWithActiveRegionalClient } from '~/app/regionalApiClient';
import {
  type YooKassaCheckoutContract,
  createBillingRepository,
} from '~/repositories/billingRepository';
import {
  useActiveWorkspace,
  useWorkspaceBillingActivity,
  useWorkspaceBillingSummary,
} from '~/stores/workspace';
import { BillingDialogs } from './BillingDialogs';
import { BillingOverview } from './BillingOverview';
import {
  readBillingActivityState,
  setBillingActivityCursor,
  setBillingActivityType,
} from './billingActivityState';
import {
  type BillingDialog,
  readBillingDialog,
  setBillingDialog,
} from './billingDialogState';
import { CreditUsageTable } from './CreditUsageTable';

const repository = createBillingRepository(requestWithActiveRegionalClient);

export const BillingPageView = ({
  summary,
  activityPage = null,
  activityType = null,
  activityLoading = false,
  activityError = '',
  onActivityTypeChange = () => undefined,
  onActivityCursorChange = () => undefined,
  onCheckout,
  onAbandonCheckout,
  onCreditCheckout,
}: {
  summary: WorkspaceBillingContract;
  activityPage?: WorkspaceBillingActivityPageContract | null;
  activityType?: WorkspaceBillingActivityType | null;
  activityLoading?: boolean;
  activityError?: string;
  onActivityTypeChange?: (type: WorkspaceBillingActivityType | null) => void;
  onActivityCursorChange?: (cursor: string | null) => void;
  onCheckout?: (
    plan: 'STANDARD' | 'PRO' | 'MAX',
    billingCadence: SubscriptionBillingCadence
  ) => Promise<YooKassaCheckoutContract | null>;
  onAbandonCheckout?: (orderId: string) => Promise<{ status: string }>;
  onCreditCheckout?: () => Promise<void>;
}): React.ReactElement => {
  const { t } = useTranslation('billing');
  const [searchParams, setSearchParams] = useSearchParams();
  const dialog = readBillingDialog(`?${searchParams.toString()}`);
  const setDialog = (next: BillingDialog | null): void => {
    const search = setBillingDialog(`?${searchParams.toString()}`, next);
    setSearchParams(new URLSearchParams(search), { replace: false });
  };

  return (
    <div className="grid gap-8">
      <header>
        <h1 className="text-balance text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          {t('title')}
        </h1>
        <p className="mt-2 text-sm text-muted">
          {t('sharedBy', { workspace: summary.workspace.name })}
        </p>
      </header>
      <BillingOverview
        onGetCredits={() =>
          setDialog(
            summary.subscription.plan === 'FREE' ? 'upgrade' : 'credits'
          )
        }
        onUpgrade={() => setDialog('upgrade')}
        summary={summary}
      />
      <CreditUsageTable
        activityType={activityType}
        error={activityError}
        loading={activityLoading}
        onCursorChange={onActivityCursorChange}
        onTypeChange={onActivityTypeChange}
        page={activityPage}
      />

      <BillingDialogs
        dialog={dialog}
        onClose={() => setDialog(null)}
        onCheckout={onCheckout}
        onAbandonCheckout={onAbandonCheckout}
        onCreditCheckout={onCreditCheckout}
        summary={summary}
      />
    </div>
  );
};

export const BillingPage = (): React.ReactElement => {
  const { t } = useTranslation('billing');
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const search = `?${searchParams.toString()}`;
  const activityState = readBillingActivityState(search);
  const {
    summary,
    loading: summaryLoading,
    error,
  } = useWorkspaceBillingSummary(workspaceId);
  const {
    page: activityPage,
    loading: activityLoading,
    error: activityError,
  } = useWorkspaceBillingActivity(workspaceId, {
    type: activityState.type,
    cursor: activityState.cursor,
    enabled: Boolean(workspaceId) && !activityState.invalidType,
    onInvalidCursor: () => {
      setSearchParams(
        new URLSearchParams(setBillingActivityCursor(search, null)),
        { replace: true }
      );
    },
  });

  useEffect(() => {
    if (!activityState.invalidType) return;
    setSearchParams(new URLSearchParams(setBillingActivityType(search, null)), {
      replace: true,
    });
  }, [activityState.invalidType, search, setSearchParams]);

  if (!activeWorkspace)
    return <p className="text-sm text-muted">{t('chooseWorkspace')}</p>;
  if (error)
    return (
      <section>
        <h2 className="text-xl font-semibold text-foreground">
          {t('loadError')}
        </h2>
        <p className="mt-2 text-sm text-muted">{error}</p>
      </section>
    );
  if (summaryLoading || !summary || summary.workspace.id !== activeWorkspace.id)
    return (
      <p aria-live="polite" className="text-sm text-muted">
        {t('loading')}
      </p>
    );
  return (
    <BillingPageView
      activityError={activityError || ''}
      activityLoading={activityLoading}
      activityPage={activityPage}
      activityType={activityState.type}
      onActivityCursorChange={cursor =>
        setSearchParams(
          new URLSearchParams(setBillingActivityCursor(search, cursor))
        )
      }
      onActivityTypeChange={type =>
        setSearchParams(
          new URLSearchParams(setBillingActivityType(search, type))
        )
      }
      onCheckout={async (plan, billingCadence) => {
        const checkout = await repository.createYooKassaCheckout(
          activeWorkspace.id,
          plan,
          billingCadence,
          crypto.randomUUID()
        );
        if (checkout.action === 'RESOLVE_PENDING') return checkout;
        if (!checkout.confirmationUrl) throw new Error(t('checkoutError'));
        window.location.assign(checkout.confirmationUrl);
        return null;
      }}
      onAbandonCheckout={async orderId => {
        return repository.abandonYooKassaOrder(activeWorkspace.id, orderId);
      }}
      onCreditCheckout={async () => {
        const checkout = await repository.createYooKassaCreditCheckout(
          activeWorkspace.id,
          crypto.randomUUID()
        );
        if (!checkout.confirmationUrl) throw new Error(t('checkoutError'));
        window.location.assign(checkout.confirmationUrl);
      }}
      summary={summary}
    />
  );
};
